/**
 * Toasts: a small, dependency-free queue with a polite live region (React Aria's toast API is
 * still UNSTABLE_). Toasts never carry financial truth on their own (DESIGN_SYSTEM §7).
 */
import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cx } from '../cx.ts';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';
interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}
interface ToastApi {
  show: (t: Omit<ToastItem, 'id'>) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const ICONS = { info: Info, success: CheckCircle2, warning: TriangleAlert, danger: TriangleAlert };
const TONES = {
  info: 'text-info',
  success: 'text-positive',
  warning: 'text-pending',
  danger: 'text-negative',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (t: Omit<ToastItem, 'id'>) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-2), { ...t, id }]);
      // Errors stay until dismissed; others auto-dismiss after 4s (MOTION_SYSTEM §3).
      if (t.tone !== 'danger') window.setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );
  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext value={api}>
      {children}
      <div
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed inset-x-0 top-3 z-[60] flex flex-col items-center gap-2 px-4 lg:top-auto lg:bottom-6 lg:items-start lg:ps-6"
      >
        {items.map((t) => {
          const Icon = ICONS[t.tone];
          return (
            <div
              key={t.id}
              role={t.tone === 'danger' ? 'alert' : 'status'}
              className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border border-line-strong bg-surface-2 p-3.5 shadow-float animate-[hx-rise-in_var(--hx-duration-base)_var(--hx-ease-enter)]"
            >
              <Icon aria-hidden className={cx('mt-0.5 size-5 shrink-0', TONES[t.tone])} />
              <div className="min-w-0 flex-1">
                <p className="m-0 text-sm font-semibold text-fg">{t.title}</p>
                {t.description && (
                  <p className="m-0 mt-0.5 text-xs leading-6 text-fg-muted">{t.description}</p>
                )}
              </div>
              <button
                type="button"
                aria-label="بستن اعلان"
                onClick={() => dismiss(t.id)}
                className="grid size-7 place-items-center rounded-xs text-fg-subtle hover:text-fg cursor-pointer"
              >
                <X aria-hidden className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
